export type Accidental = '#' | 'b';
export type NotationType = 'letter' | 'solfege';

export class Note {
  private static instances: Map<number, Note> = new Map();

  private constructor(public value: number) {}

  static create(value: number): Note {
    if (value < 0 || value > 127) {
      throw new Error('Note value must be between 0 and 127');
    }
    if (!Note.instances.has(value)) {
      Note.instances.set(value, new Note(value));
    }

    return Note.instances.get(value)!;
  }

  valueOf(): number {
    return this.value;
  }

  next(): Note {
    if (this.value === 127) {
      throw new Error('Note value cannot exceed 127');
    }
    return Note.create(this.value + 1);
  }

  prev(): Note {
    if (this.value === 0) {
      throw new Error('Note value cannot be less than 0');
    }
    return Note.create(this.value - 1);
  }

  transpose(steps: number): Note {
    const newValue = this.value + steps;
    if (newValue < 0 || newValue > 127) {
      throw new Error('Note value must be between 0 and 127');
    }
    return Note.create(newValue);
  }

  norm(): Note {
    return Note.create(this.value % 12);
  }

  equals(other: Note): boolean {
    return this.value === other.value;
  }

    toString(
        includeOctave = false,
        accidental: Accidental | 'random' = 'random',
    ): string {
    const noteNames = 'C D EF G A B';
    let name = noteNames[this.value % 12];
    if (name === ' ') {
      if (accidental === 'random') {
        accidental = Math.random() < 0.5 ? '#' : 'b';
      }
      if (accidental === '#') {
        name = `${noteNames[(this.value - 1) % 12]}♯`;
      } else {
        name = `${noteNames[(this.value + 1) % 12]}♭`;
      }
    }
    if (includeOctave) {
      const octave = Math.floor(this.value / 12) - 1;
      if (octave >= 0) {
        name += octave;
      }
    }
    return name;
  }

  static fromNumber(num: number): Note {
    return Note.create(num);
  }

  static fromString(noteStr: string): Note {
    const noteNames = 'C D EF G A B';
    const noteIndex = noteNames.indexOf(noteStr[0]);
    if (noteIndex === -1) {
      throw new Error(`Invalid note name: ${noteStr}`);
    }
    let value = noteIndex;
    if (noteStr.length > 1) {
      const accidental = noteStr[1];
      if (accidental === '#' || accidental === 's') {
        value += 1;
      } else if (accidental === 'b') {
        value += 11;
        value %= 12;
      }
    }
    return Note.create(value);
  }
}


export class NoteSet implements Iterable<Note> {
  private notes: Note[] = [];

  constructor(initialValues?: Iterable<Note>) {
    if (initialValues) {
      for (const value of initialValues) {
        this.add(value);
      }
    }
  }

  add(note: Note): this {
    const index = this.binarySearch(note.value);
    if (index >= this.notes.length || this.notes[index].value !== note.value) {
      this.notes.splice(index, 0, note);
    }
    return this;
  }

  has(note: Note): boolean {
    const index = this.binarySearch(note.value);
    if (index >= this.notes.length) {
      return false;
    }
    return this.notes[index].value === note.value;
  }

  delete(note: Note): boolean {
    const index = this.binarySearch(note.value);
    if (this.notes[index]?.value === note.value) { // Add optional chaining
      this.notes.splice(index, 1);
      return true;
    }
    return false;
  }

  clear(): void {
    this.notes = [];
  }

  get size(): number {
    return this.notes.length;
  }

  private binarySearch(value: number): number {
    let low = 0;
    let high = this.notes.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (this.notes[mid].value < value) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }

  [Symbol.iterator](): Iterator<Note> {
    return this.notes[Symbol.iterator]();
  }

  forEach(callbackfn: (value: Note, index: number, array: Note[]) => void): void {
    this.notes.forEach(callbackfn);
  }

  map<U>(callbackfn: (value: Note, index: number, array: Note[]) => U): U[] {
    return this.notes.map(callbackfn);
  }

  //filter(predicate: (value: Note, index: number, array: Note[]) => boolean): Note[] {
    //return this.notes.filter(predicate);
  //}

  get bottomNote(): Note | undefined {
    return this.notes.length > 0 ? this.notes[0] : undefined;
  }

  get topNote(): Note | undefined {
    return this.notes.length > 0 ? this.notes[this.notes.length - 1] : undefined;
  }

  toString(): string {
      return this.notes.map(note => note.toString(true, '#')).join(', ');
  }
}
